import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Separator } from '@/components/ui/separator';
import { Users, Plus, Calendar, AlertTriangle, BarChart3, Network, Edit, Trash2, User, Eye } from 'lucide-react';
import { format } from 'date-fns';
import { LinkManager } from '@/components/shared/LinkManager';
import { EntityLinkedPreview } from '@/components/shared/EntityLinkedPreview';
import { SupplierEditDialog } from '@/components/suppliers/SupplierEditDialog';
import { SupplierCreateDialog } from '@/components/suppliers/SupplierCreateDialog';
import { useSuppliers } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { useLinkStore } from '@/stores/linkStore';

const criticalityColors: Record<string, string> = {
  'Very Low': 'bg-muted text-muted-foreground',
  'Low': 'bg-success text-success-foreground',
  'Medium': 'bg-warning text-warning-foreground',
  'High': 'bg-orange-500 text-white',
  'Very High': 'bg-danger text-danger-foreground',
  // Legacy mappings
  'low': 'bg-success text-success-foreground',
  'medium': 'bg-warning text-warning-foreground',
  'high': 'bg-orange-500 text-white',
  'critical': 'bg-danger text-danger-foreground',
};

const criticalityLabels: Record<string, string> = {
  'Very Low': 'Very Low',
  'Low': 'Low',
  'Medium': 'Medium',
  'High': 'High',
  'Very High': 'Very High',
  // Legacy mappings
  'low': 'Low',
  'medium': 'Medium',
  'high': 'High',
  'critical': 'Very High',
};

export default function SuppliersPage() {
  const { organization, profile } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const filterAssigned = searchParams.get('filter') === 'assigned';
  const [criticalityFilter, setCriticalityFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [selectedSupplierId, setSelectedSupplierId] = useState<string | null>(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);

  const { loadLinks } = useLinkStore();

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const {
    suppliers,
    isLoading,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    isCreating,
    isDeleting,
  } = useSuppliers(organization?.id, { criticality: criticalityFilter !== 'all' ? criticalityFilter : undefined, category: categoryFilter !== 'all' ? categoryFilter : undefined });

  const selectedSupplier = suppliers?.find(s => s.id === selectedSupplierId);

  const handleCreate = async (data: any, links?: Array<{id: string; type: any; name: string}>) => {
    if (!organization?.id) return;
    
    createSupplier({
      ...data,
      org_id: organization.id,
    }, {
      onSuccess: async (newSupplier: any) => {
        if (newSupplier?.id && links && links.length > 0) {
          const { useLinkStore } = await import('@/stores/linkStore');
          const { bulkCreateLinks } = useLinkStore.getState();
          
          for (const link of links) {
            await bulkCreateLinks('supplier', newSupplier.id, link.type, [link.id]);
          }
        }
      }
    });

    setAddDialogOpen(false);
  };

  const handleDelete = () => {
    if (!selectedSupplierId) return;
    deleteSupplier(selectedSupplierId);
    setDeleteDialogOpen(false);
    setSelectedSupplierId(null);
    setDetailSheetOpen(false);
  };

  const openDetailSheet = (supplierId: string) => {
    setSelectedSupplierId(supplierId);
    setDetailSheetOpen(true);
  };

  const uniqueCategories = suppliers ? [...new Set(suppliers.map(s => s.category).filter(Boolean) as string[])] : [];

  // Filter suppliers by owner if assigned filter is active
  const filteredSuppliers = suppliers?.filter(supplier => {
    if (filterAssigned && supplier.owner_id !== profile?.user_id) return false;
    return true;
  }) || [];

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Suppliers</h1>
            <p className="text-muted-foreground">Manage third-party suppliers and vendors</p>
          </div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Suppliers</h1>
          <p className="text-muted-foreground">Manage third-party suppliers and vendors</p>
        </div>
        <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
          <Plus className="h-4 w-4" />
          Add Supplier
        </Button>
      </div>

      <div className="flex gap-4">
        <Select value={criticalityFilter} onValueChange={setCriticalityFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by criticality" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Criticality Levels</SelectItem>
            <SelectItem value="Very High">Very High</SelectItem>
            <SelectItem value="High">High</SelectItem>
            <SelectItem value="Medium">Medium</SelectItem>
            <SelectItem value="Low">Low</SelectItem>
            <SelectItem value="Very Low">Very Low</SelectItem>
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {uniqueCategories.map((category) => (
              <SelectItem key={category} value={category}>
                {category}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          variant={filterAssigned ? "default" : "outline"}
          size="sm"
          onClick={() => {
            if (filterAssigned) {
              searchParams.delete('filter');
            } else {
              searchParams.set('filter', 'assigned');
            }
            setSearchParams(searchParams);
          }}
        >
          <User className="h-4 w-4 mr-2" />
          Assigned to me
        </Button>
      </div>

      {filteredSuppliers && filteredSuppliers.length > 0 ? (
        <div className="space-y-4">
          {filteredSuppliers.map((supplier) => (
            <Card 
              key={supplier.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => openDetailSheet(supplier.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Users className="h-5 w-5 text-foreground" />
                      <CardTitle className="text-lg">{supplier.name}</CardTitle>
                      <Badge className={criticalityColors[supplier.criticality as keyof typeof criticalityColors]}>
                        {criticalityLabels[supplier.criticality as keyof typeof criticalityLabels]}
                      </Badge>
                    </div>
                    {supplier.category && (
                      <CardDescription>{supplier.category}</CardDescription>
                    )}
                    {/* Show linked entities */}
                    <div className="mt-2">
                      <EntityLinkedPreview entityType="supplier" entityId={supplier.id} maxDisplay={3} />
                    </div>
                  </div>
                  <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openDetailSheet(supplier.id)}
                      title="View details"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedSupplierId(supplier.id);
                        setEditDialogOpen(true);
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedSupplierId(supplier.id);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  {(supplier as any).owner && (
                    <div>
                      <p className="text-muted-foreground">Owner</p>
                      <p className="font-medium">{(supplier as any).owner.name || (supplier as any).owner.email}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-muted-foreground">Issues</p>
                    <div className="flex items-center gap-1">
                      {(supplier.issues_count || 0) > 0 && <AlertTriangle className="h-4 w-4 text-danger" />}
                      <span className={(supplier.issues_count || 0) > 0 ? 'text-danger font-medium' : 'font-medium'}>
                        {supplier.issues_count || 0}
                      </span>
                    </div>
                  </div>
                  {supplier.questionnaire_score && (
                    <div>
                      <p className="text-muted-foreground">Score</p>
                      <div className="flex items-center gap-1">
                        <BarChart3 className="h-4 w-4 text-foreground" />
                        <span className="font-medium">{supplier.questionnaire_score}/100</span>
                      </div>
                    </div>
                  )}
                  {supplier.next_review && (
                    <div>
                      <p className="text-muted-foreground">Next Review</p>
                      <p className="font-medium">{format(new Date(supplier.next_review), 'MMM dd, yyyy')}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No suppliers found</h3>
            <p className="text-muted-foreground text-center mb-4">
              Start managing your supply chain by adding supplier information.
            </p>
            <Button className="gap-2" onClick={() => setAddDialogOpen(true)}>
              <Plus className="h-4 w-4" />
              Add Your First Supplier
            </Button>
          </CardContent>
        </Card>
      )}

      <SupplierCreateDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        onSave={handleCreate}
        isCreating={isCreating}
      />

      {/* Detail Sheet */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <div className="flex items-center justify-between">
              <SheetTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Supplier Details
              </SheetTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setDetailSheetOpen(false);
                    setEditDialogOpen(true);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDeleteDialogOpen(true)}
                  disabled={isDeleting}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          </SheetHeader>

          {selectedSupplier && (
            <div className="space-y-6 py-6">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-foreground" />
                  <h3 className="text-lg font-semibold">{selectedSupplier.name}</h3>
                </div>
                <Badge className={criticalityColors[selectedSupplier.criticality as keyof typeof criticalityColors]}>
                  {criticalityLabels[selectedSupplier.criticality as keyof typeof criticalityLabels]}
                </Badge>
                {selectedSupplier.category && (
                  <p className="text-sm text-muted-foreground mt-2">{selectedSupplier.category}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Issues Count</p>
                  <div className="flex items-center gap-1 mt-1">
                    {(selectedSupplier.issues_count || 0) > 0 && <AlertTriangle className="h-4 w-4 text-danger" />}
                    <span className={(selectedSupplier.issues_count || 0) > 0 ? 'text-danger font-medium' : 'font-medium'}>
                      {selectedSupplier.issues_count || 0}
                    </span>
                  </div>
                </div>

                {selectedSupplier.questionnaire_score && (
                  <div>
                    <p className="text-muted-foreground">Questionnaire Score</p>
                    <div className="flex items-center gap-1 mt-1">
                      <BarChart3 className="h-4 w-4 text-foreground" />
                      <span className="font-medium">{selectedSupplier.questionnaire_score}/100</span>
                    </div>
                  </div>
                )}

                {selectedSupplier.next_review && (
                  <div>
                    <p className="text-muted-foreground">Next Review</p>
                    <div className="flex items-center gap-1 mt-1">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{format(new Date(selectedSupplier.next_review), 'MMM dd, yyyy')}</span>
                    </div>
                  </div>
                )}

                  {selectedSupplier.last_assessment && (
                    <div>
                      <p className="text-muted-foreground">Last Assessment</p>
                      <span className="font-medium">{format(new Date(selectedSupplier.last_assessment), 'MMM dd, yyyy')}</span>
                    </div>
                  )}
                </div>

              {selectedSupplier.notes && (
                <div>
                  <h4 className="text-sm font-medium mb-2">Notes</h4>
                  <p className="text-sm text-muted-foreground">{selectedSupplier.notes}</p>
                </div>
              )}

              <Separator />

              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Network className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">Related Entities</h4>
                </div>
                <LinkManager 
                  entityType="supplier"
                  entityId={selectedSupplier.id}
                  entityName={selectedSupplier.name}
                  showHeader={false}
                  allowedTypes={['site', 'zone', 'system', 'endpoint', 'cloud_resource', 'risk', 'control', 'evidence', 'incident', 'task']}
                />
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <SupplierEditDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        supplier={selectedSupplier}
        onSave={(id, data) => updateSupplier({ id, data })}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Supplier</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedSupplier?.name}"? This action cannot be undone and will remove all associated links and access relations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-danger text-danger-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
